import { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Visual pull-to-refresh indicator for iOS WebView wrappers
 * that don't show a native refresh spinner.
 */
export const PullToRefreshIndicator = () => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const isTracking = useRef(false);

  const THRESHOLD = 80;

  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Only track when scrolled to top
    if (window.scrollY <= 0) {
      startY.current = e.touches[0].clientY;
      isTracking.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isTracking.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    if (diff > 10 && window.scrollY <= 0) {
      setPulling(true);
      setPullDistance(Math.min(diff * 0.4, 120));
    } else {
      setPulling(false);
      setPullDistance(0);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      // Reload after a brief visual delay
      setTimeout(() => {
        window.location.reload();
      }, 400);
    } else {
      setPulling(false);
      setPullDistance(0);
    }
    isTracking.current = false;
  }, [pullDistance]);

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  if (!pulling && !refreshing) return null;

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = pullDistance * 3;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex justify-center pointer-events-none"
      style={{
        transform: `translateY(${pullDistance - 40}px)`,
        transition: pulling ? 'none' : 'transform 0.3s ease-out',
      }}
    >
      <div
        className="rounded-full bg-background shadow-lg border p-2"
        style={{
          opacity: progress,
          transform: `rotate(${rotation}deg)`,
        }}
      >
        <RefreshCw
          className={`h-5 w-5 text-primary ${refreshing ? 'animate-spin' : ''}`}
        />
      </div>
    </div>
  );
};
